import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button, { ButtonType } from 'components/Button';
import Loader from 'components/Loader';
import { closeAIChat } from 'handlers/aiChat';
import { AiCoachConversationRole } from 'handlers/aiCoachConversation';
import { RoutePath } from 'enums/Routes';
import { isConfirmedLoanOffer, isProbableLoanOffer } from 'components/FinancialCheckup/loanOfferAccess';
import ProbableOfferMark from 'components/FinancialCheckup/ProbableOfferMark/ProbableOfferMark';
import usePursueLoanOffer from 'hooks/usePursueLoanOffer';
import { getApplicationData } from 'selectors/getApplicationData';
import useBodyOverflow from 'hooks/useBodyOverflow';
import useAiCoachConversation from 'hooks/useAiCoachConversation';
import { useNavigate } from 'hooks/useNavigate';
import { OFFER_AVAILABLE_FROM_DASHBOARD_STATE } from 'utils/navigationStates';

import { ReactComponent as AiIconAvatar } from 'images/ai-stars-icon.svg';

import styles from './Conversation.module.scss';

const MAX_OPTIONS = 4;
const BUBBLE_REVEAL_DELAY_MS = 800;
const DOTS_LOADER_SIZE = 25;

interface ConversationWorkflowProps {
  scrollHeightRef?: React.RefObject<HTMLDivElement>;
}

const ConversationWorkflow = ({ scrollHeightRef }: ConversationWorkflowProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { application } = useSelector(getApplicationData);
  const isOfferAvailable = isConfirmedLoanOffer(application?.status);
  const isProbableOffer = isProbableLoanOffer(application?.status);
  const { pursueLoan } = usePursueLoanOffer();
  const { entries, options, terminal, loading, error, selectOption, retry } = useAiCoachConversation(application?.id);
  // Entries already present on mount (e.g. when reopening the modal) show instantly;
  // only entries added afterwards animate in.
  const [revealedCount, setRevealedCount] = useState(() => entries.length);

  useBodyOverflow(true);

  // Reveal the transcript one entry at a time. The user's tapped option shows
  // instantly, while assistant bubbles fade in with the three-dots loader in between.
  useEffect(() => {
    if (revealedCount >= entries.length) return undefined;

    const nextEntry = entries[revealedCount];
    const delay = nextEntry.role === AiCoachConversationRole.User ? 0 : BUBBLE_REVEAL_DELAY_MS;
    const timer = setTimeout(() => setRevealedCount((count) => count + 1), delay);
    return () => clearTimeout(timer);
  }, [revealedCount, entries]);

  // Keep the latest bubble, thinking loader and options in view as the chat grows.
  useEffect(() => {
    scrollHeightRef?.current?.scrollTo({
      top: scrollHeightRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [revealedCount, loading, options, terminal, error, scrollHeightRef]);

  const allBubblesRevealed = revealedCount >= entries.length;
  const isRevealing = !allBubblesRevealed && !loading;
  const showOptions = allBubblesRevealed && !loading && !error && !terminal && options.length > 0;
  const showTerminalNote = allBubblesRevealed && !loading && !error && terminal;

  const handleTerminalLinkClick = () => {
    dispatch(closeAIChat());

    if (isProbableOffer) {
      pursueLoan();
      return;
    }

    if (isOfferAvailable) {
      navigate(RoutePath.OfferAvailable, OFFER_AVAILABLE_FROM_DASHBOARD_STATE);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.messages}>
        {entries.slice(0, revealedCount).map((entry, index) =>
          entry.role === AiCoachConversationRole.User ? (
            <p key={index} className={styles.userChoice}>
              {entry.text}
            </p>
          ) : (
            <div key={index} className={styles.bubbleContainer}>
              <div className={styles.bubbleAvatar}>
                <AiIconAvatar className={styles.aiIconAvatar} />
              </div>
              <p className={styles.bubble}>{entry.text}</p>
            </div>
          ),
        )}
        {(isRevealing || loading) && (
          <div className={styles.bubbleContainer}>
            <div className={styles.bubbleAvatar}>
              <AiIconAvatar className={styles.aiIconAvatar} />
            </div>
            <div className={styles.bubble}>
              <Loader size={DOTS_LOADER_SIZE} />
              <p className={styles.thinkingText}>Plannery is thinking...</p>
            </div>
          </div>
        )}
      </div>

      {showOptions && (
        <div className={styles.optionsContainer}>
          {options.slice(0, MAX_OPTIONS).map((option, index) => (
            <Button
              key={option.id}
              type={ButtonType.ChatOptionButton}
              className={styles.promptButton}
              onClick={() => selectOption(option.id)}
              style={{ '--index': index } as React.CSSProperties}
            >
              {option.label}
            </Button>
          ))}
        </div>
      )}

      {error && !loading && (
        <div className={styles.error}>
          <p className={styles.errorText}>Something went wrong. Please try again.</p>
          <Button type={ButtonType.Inverted} onClick={retry}>
            Try again
          </Button>
        </div>
      )}

      {showTerminalNote && (
        <p className={styles.terminalNote}>
          That's all for now.{' '}
          <span className={styles.terminalLink} onClick={handleTerminalLinkClick}>
            {isOfferAvailable || isProbableOffer ? 'See my offer' : 'Check my finances'}
            {isProbableOffer && <ProbableOfferMark />}
          </span>
        </p>
      )}
    </div>
  );
};

export default ConversationWorkflow;
